import * as PIXI from 'pixi.js';
import isEqual from 'lodash/isEqual';

import {
    assertArray,
    assertMinimum,
    assertMinimumLength,
    assertNumber,
    assertNumberBetween,
} from '../../validators';
import { combineRect, getMidPointFromRect, getRectFromPoints } from '../../libs/padding';
import { drawRoundedPolygon } from '../../libs/roundedPolygonPath';
import type { Point, Resolution } from '../../types';
import type { MaskEdge, RelocatedMaskData } from './StencilMask';
import { StencilMask } from './StencilMask';
import { observable } from '../reactivity';

type PolygonMaskMorphState = {
    startPoints: number[];
    endPoints: number[];
    startCornerRadii: number[];
    endCornerRadii: number[];
};

/**
 * Smallest span a resize may leave on either axis, so dragging an edge past the opposite one
 * flattens the shape rather than turning it inside out.
 */
const MIN_NORMALIZED_SPAN = 0.01;

type PointType = typeof PolygonMask.START_POINTS | typeof PolygonMask.END_POINTS;
/**
 * The PolygonMask Object will create a mask for an arbitrary shape, defined by an array of point coordinates.
 */
export class PolygonMask extends StencilMask {
    override _classType = 'PolygonMask';

    static readonly START_POINTS = 'startPoints';
    static readonly END_POINTS = 'endPoints';

    @observable.ref accessor #startPoints: number[] = [0.9, 0.1, 0.9, 0.9, 0.1, 0.9, 0.1, 0.1];
    @observable.ref accessor #endPoints: number[] = [0.9, 0.1, 0.9, 0.9, 0.1, 0.9, 0.1, 0.1];
    @observable.ref accessor #startCornerRadii: number[] = [];
    @observable.ref accessor #endCornerRadii: number[] = [];
    // TODO(LUM-662): make the overlay fields observable and read them in the helper rebuild reactions.
    #overlayPointType: PointType = PolygonMask.START_POINTS;
    #overlaySelectedPointIndex: number | null = null;

    #morphState: PolygonMaskMorphState | null = null;

    /**
     * An array representing x/y coordinates of the points that define the object's shape at it's starting state.
     * e.g. [x1, y1, x2, y2, x3, y3 ... ]
     */
    get startPoints(): readonly number[] {
        return this.#startPoints.slice();
    }

    set startPoints(points: readonly number[]) {
        if (isEqual(this.#startPoints, points)) {
            return;
        }
        assertArray(points);
        assertMinimumLength(points, 2);
        this.#startPoints = points.slice();
        this.requiresUpdate = true;
    }

    /**
     * An array representing x/y coordinates of the points that define the object's shape at it's ending state.
     * e.g. [x1, y1, x2, y2, x3, y3 ... ]
     */
    get endPoints(): readonly number[] {
        return this.#endPoints.slice();
    }

    set endPoints(points: readonly number[]) {
        if (isEqual(this.#endPoints, points)) {
            return;
        }
        assertArray(points);
        assertMinimumLength(points, 2);
        this.#endPoints = points.slice();
        this.requiresUpdate = true;
    }

    /**
     * The radius of each corner at the shape's starting state, in reference resolution pixels, in the same
     * order as the points. A corner without a radius is sharp. Each radius is reduced where it doesn't fit
     * along the corner's edges.
     */
    get startCornerRadii(): readonly number[] {
        return this.#startCornerRadii.slice();
    }

    set startCornerRadii(radii: readonly number[]) {
        if (isEqual(this.#startCornerRadii, radii)) {
            return;
        }
        assertCornerRadii(radii);
        this.#startCornerRadii = radii.slice();
        this.requiresUpdate = true;
    }

    /**
     * The radius of each corner at the shape's ending state, in reference resolution pixels, in the same
     * order as the points. A corner without a radius is sharp. Each radius is reduced where it doesn't fit
     * along the corner's edges.
     */
    get endCornerRadii(): readonly number[] {
        return this.#endCornerRadii.slice();
    }

    set endCornerRadii(radii: readonly number[]) {
        if (isEqual(this.#endCornerRadii, radii)) {
            return;
        }
        assertCornerRadii(radii);
        this.#endCornerRadii = radii.slice();
        this.requiresUpdate = true;
    }

    /**
     * @internal
     */
    _getCornerRadiusPerPoint(pointType: PointType): number[] {
        const radii =
            pointType === PolygonMask.START_POINTS ? this.#startCornerRadii : this.#endCornerRadii;
        return Array.from({ length: this.#startPoints.length >> 1 }, (_, i) => radii[i] ?? 0);
    }

    setOverlaySelectedPoint(pointType: PointType, pointIndex: number | null) {
        if (
            this.#overlayPointType === pointType &&
            this.#overlaySelectedPointIndex === pointIndex
        ) {
            return;
        }
        this.#overlayPointType = pointType;
        this.#overlaySelectedPointIndex = pointIndex;
        this.requiresUpdate = true;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'startPoints',
            'endPoints',
            'startCornerRadii',
            'endCornerRadii',
        ]);
    }

    /**
     * Shifts every start and end point, so the shape translates without deforming. Points are
     * normalized against the reference resolution, so the pixel delta is converted per axis.
     */
    override moveBy(deltaX: number, deltaY: number) {
        const { width, height } = this._getReferenceResolution();
        const normalizedX = deltaX / width;
        const normalizedY = deltaY / height;
        const shift = (points: number[]) =>
            points.map((value, i) => value + (i % 2 ? normalizedY : normalizedX));

        this.startPoints = shift(this.#startPoints);
        this.endPoints = shift(this.#endPoints);
    }

    override _saveRelocated(from: Resolution, to: Resolution): RelocatedMaskData {
        const bounds = combineRect([
            getRectFromPoints(this.#startPoints, from),
            getRectFromPoints(this.#endPoints, from),
        ]);
        const center = getMidPointFromRect(bounds);
        const deltaX = (center.x / from.width) * to.width - center.x;
        const deltaY = (center.y / from.height) * to.height - center.y;
        const relocate = (points: number[]) =>
            points.map((value, i) =>
                i % 2
                    ? (value * from.height + deltaY) / to.height
                    : (value * from.width + deltaX) / to.width,
            );
        return {
            data: {
                ...this.save(),
                startPoints: relocate(this.#startPoints),
                endPoints: relocate(this.#endPoints),
            },
            deltaX,
            deltaY,
        };
    }

    /**
     * Scales the points along the dragged edge's axis, anchored at the opposite edge. The anchor
     * and scale come from the combined extents of the start and end points and apply to both
     * arrays, so an animated mask scales rigidly instead of deforming as it plays.
     */
    override resizeBy(edge: MaskEdge, delta: number) {
        const { width, height } = this._getReferenceResolution();
        const isHorizontal = edge === 'left' || edge === 'right';
        const normalizedDelta = delta / (isHorizontal ? width : height);
        // the near edge (left/top) is the axis minimum, so dragging it inward shrinks the span
        const movesNearEdge = edge === 'left' || edge === 'top';
        const axisOffset = isHorizontal ? 0 : 1;

        const axisValues = [...this.#startPoints, ...this.#endPoints].filter(
            (_, i) => i % 2 === axisOffset,
        );
        const min = Math.min(...axisValues);
        const max = Math.max(...axisValues);
        const span = max - min;
        if (span <= 0) {
            return;
        }

        const anchor = movesNearEdge ? max : min;
        const newSpan = Math.max(
            MIN_NORMALIZED_SPAN,
            movesNearEdge ? span - normalizedDelta : span + normalizedDelta,
        );
        const scale = newSpan / span;
        const transform = (points: number[]) =>
            points.map((value, i) =>
                i % 2 === axisOffset ? anchor + (value - anchor) * scale : value,
            );

        this.startPoints = transform(this.#startPoints);
        this.endPoints = transform(this.#endPoints);
    }

    override getMaxBounds(): PIXI.Rectangle {
        const referenceResolution = this._getReferenceResolution();
        if (this.#morphState) {
            const { startPoints, endPoints } = this.#morphState;
            const t = this.transition;
            const interpolated = startPoints.map((p, i) => p * (1 - t) + endPoints[i] * t);
            const rect = getRectFromPoints(interpolated, referenceResolution);
            return new PIXI.Rectangle(rect.x, rect.y, rect.width, rect.height);
        }
        const startRect = getRectFromPoints(this.#startPoints, referenceResolution);
        const endRect = getRectFromPoints(this.#endPoints, referenceResolution);
        const combined = combineRect([startRect, endRect]);
        return new PIXI.Rectangle(combined.x, combined.y, combined.width, combined.height);
    }

    /**
     * Draw the polygon shape, using the transition value to specify the point between the shape's start and end x/y
     * coordinate states.
     */
    override updateForRender() {
        super.updateForRender();
        const startPoints = this.#morphState?.startPoints ?? this.#startPoints;
        const endPoints = this.#morphState?.endPoints ?? this.#endPoints;
        const startCornerRadii = this.#morphState?.startCornerRadii ?? this.#startCornerRadii;
        const endCornerRadii = this.#morphState?.endCornerRadii ?? this.#endCornerRadii;

        const referenceResolution = this._getReferenceResolution();
        const points = startPoints.map((_, i) => {
            let value = startPoints[i] * (1 - this.transition);
            value += endPoints[i] * this.transition;
            // TODO: bake down start and end points so they are no longer dependent on root
            value *= i % 2 ? referenceResolution.height : referenceResolution.width;
            value += i % 2 ? this.y : this.x;
            return value;
        });

        const cornerRadii = Array.from(
            { length: points.length >> 1 },
            (_, i) =>
                (startCornerRadii[i] ?? 0) * (1 - this.transition) +
                (endCornerRadii[i] ?? 0) * this.transition,
        );

        this.clear();
        drawRoundedPolygon(this, points, cornerRadii);
        this.fill({ color: 0xffffff, alpha: 1 });
    }

    override createHelpers(): PIXI.Container[] {
        const helpers = super.createHelpers();

        const overlay = new PIXI.Graphics();
        overlay.label = 'PolygonMaskPointsOverlay';

        const circleRadius = 10;
        const selectedCircleRadius = 15;
        const selectedColor = 0xffff00;

        const referenceResolution = this._getReferenceResolution();
        const drawPoints = (points: number[], color: number, selectedPointInd: number | null) => {
            for (let i = 0; i < points.length; i += 2) {
                const isSelected = selectedPointInd === i / 2;
                const fillColor = isSelected ? selectedColor : color;
                const x = points[i] * referenceResolution.width + this.x;
                const y = points[i + 1] * referenceResolution.height + this.y;
                overlay.circle(x, y, isSelected ? selectedCircleRadius : circleRadius);
                overlay.fill({ color: fillColor, alpha: 1 });
            }
        };

        if (this.#overlayPointType === PolygonMask.START_POINTS) {
            drawPoints(this.#endPoints, 0x0000ff, -1);
            drawPoints(this.#startPoints, 0xff0000, this.#overlaySelectedPointIndex);
        } else {
            drawPoints(this.#startPoints, 0xff0000, -1);
            drawPoints(this.#endPoints, 0x0000ff, this.#overlaySelectedPointIndex);
        }

        helpers.push(overlay);
        return helpers;
    }

    /**
     * @internal
     */
    _setMorphState(state: PolygonMaskMorphState): void {
        this.#morphState = state;
        this.requiresUpdate = true;
    }

    /**
     * @internal
     */
    _clearMorphState(): void {
        this.#morphState = null;
        this.requiresUpdate = true;
    }

    // -------------------------------------------------------------------------
    // Point Editing API
    //
    // The API deals with an array of {x, y} points not the flat arrays of
    // this.startPoints and this.endPoints, so that it is simpler to reason
    // about editing tasks.
    //
    // Arrays are considered copy-on-write, and setting any point to an array
    // creates a new array. This means this.startPoints and this.endPoints are
    // set to new arrays.
    //
    // The operations need to identify the array that is being worked on, using
    // PolygonMask.START_POINTS, PolygonMask.END_POINTS constants.
    // -------------------------------------------------------------------------

    /**
     * Get the points for a specific array.
     *
     * @param arrayId - PolygonMask.START_POINTS or PolygonMask.END_POINTS.
     */
    getPoints(arrayId: string): Point[] {
        switch (arrayId) {
            case PolygonMask.START_POINTS:
                return toPointArray(this.#startPoints);
            case PolygonMask.END_POINTS:
                return toPointArray(this.#endPoints);
            default:
                throw Error(`Unknown point array '${arrayId}'`);
        }
    }

    /**
     * Set the points array for a specific array.
     *
     * This method replaces either this.startPoints or this.endPoints.
     *
     * @param arrayId - PolygonMask.START_POINTS or PolygonMask.END_POINTS.
     * @param array - new points array value
     */
    setPoints(pointType: PointType, array: Point[]) {
        assertArray(array);

        switch (pointType) {
            case PolygonMask.START_POINTS:
                this.startPoints = toFlatArray(array);
                break;
            case PolygonMask.END_POINTS:
                this.endPoints = toFlatArray(array);
                break;
            default:
                throw Error(`Unknown point array '${pointType}'`);
        }
    }

    /**
     * Sets a point in the identified array.
     *
     * @param pointType PolygonMask.START_POINTS or PolygonMask.END_POINTS.
     * @param index Set the point at this index, overwriting the existing point.
     * @param point The point x, y to set.
     */
    setPoint(pointType: PointType, index: number, point: Point) {
        assertNumberBetween(index, 0, (this.#startPoints.length >> 1) - 1);

        switch (pointType) {
            case PolygonMask.START_POINTS:
                {
                    const points = toPointArray(this.#startPoints);
                    points[index] = point;
                    this.startPoints = toFlatArray(points);
                }
                break;
            case PolygonMask.END_POINTS:
                {
                    const points = toPointArray(this.#endPoints);
                    points[index] = point;
                    this.endPoints = toFlatArray(points);
                }
                break;
            default:
                throw Error(`Unknown point array '${pointType}'`);
        }
    }

    /**
     * Get a point in the identified array.
     *
     * @param pointType PolygonMask.START_POINTS or PolygonMask.END_POINTS.
     * @param index Get the point at this index.
     */
    getPoint(pointType: PointType, index: number): Point {
        assertNumberBetween(index, 0, (this.#startPoints.length >> 1) - 1);

        const flatIndex = index << 1;
        let pointArray;

        switch (pointType) {
            case PolygonMask.START_POINTS:
                pointArray = this.#startPoints;
                break;
            case PolygonMask.END_POINTS:
                pointArray = this.#endPoints;
                break;
            default:
                throw Error(`Unknown point array '${pointType}'`);
        }

        return { x: pointArray[flatIndex], y: pointArray[flatIndex + 1] };
    }

    /**
     * Inserts a point into the point arrays, moving the items at the index forward by one, i.e. the existing
     * point at index i is at i+1 after insertion.
     *
     * The new point will be at the mid point of its neighbours.
     *
     * The point arrays are required to contain the same number of points. Inserting a point inserts the point
     * into all the point arrays.
     *
     * @param index Insert the point before the value at this index.
     */
    insertPoint(index: number) {
        assertNumberBetween(index, 0, this.#startPoints.length >> 1);
        assertNumberBetween(index, 0, this.#endPoints.length >> 1);

        // Because -1 % 5 = -1, and want 4.
        const mod = (a: number, b: number) => {
            const r = a % b;
            return r < 0 ? r + b : r;
        };

        type Point = { x: number; y: number };

        // Makes a point at the mid point of two points.
        const midPoint = (array: Point[], index: number) => {
            const i1 = mod(index, array.length);
            const i2 = mod(index + 1, array.length);
            return {
                x: (array[i1].x + array[i2].x) / 2,
                y: (array[i1].y + array[i2].y) / 2,
            };
        };

        const insert = <T>(array: T[], index: number, value: T) => {
            array.splice(index, 0, value);
            return array;
        };

        const array1 = toPointArray(this.#startPoints);
        const array2 = toPointArray(this.#endPoints);
        const startCornerRadii = this._getCornerRadiusPerPoint(PolygonMask.START_POINTS);
        const endCornerRadii = this._getCornerRadiusPerPoint(PolygonMask.END_POINTS);

        // Insertion is before the item at the index, therefore index - 1.
        this.startPoints = toFlatArray(insert(array1, index, midPoint(array1, index - 1)));
        this.endPoints = toFlatArray(insert(array2, index, midPoint(array2, index - 1)));
        this.startCornerRadii = insert(startCornerRadii, index, 0);
        this.endCornerRadii = insert(endCornerRadii, index, 0);
    }

    /**
     * Removes a point from the point arrays.
     *
     * The point arrays are required to contain the same number of points. Removing a point removes the point
     * from all the point arrays.
     *
     * @param index Remove the point at this index.
     */
    removePoint(index: number) {
        assertNumberBetween(index, 0, (this.#startPoints.length >> 1) - 1);
        assertNumberBetween(index, 0, (this.#endPoints.length >> 1) - 1);

        const remove = <T>(array: T[], index: number) => {
            array.splice(index, 1);
            return array;
        };

        const startCornerRadii = this._getCornerRadiusPerPoint(PolygonMask.START_POINTS);
        const endCornerRadii = this._getCornerRadiusPerPoint(PolygonMask.END_POINTS);
        this.startPoints = toFlatArray(remove(toPointArray(this.#startPoints), index));
        this.endPoints = toFlatArray(remove(toPointArray(this.#endPoints), index));
        this.startCornerRadii = remove(startCornerRadii, index);
        this.endCornerRadii = remove(endCornerRadii, index);
    }

    /**
     * Moves a point from an index to another index.
     *
     * @param fromIndex Move the point from this index.
     * @param toIndex Move the point to this index.
     */
    movePoint(fromIndex: number, toIndex: number) {
        assertNumberBetween(fromIndex, 0, (this.#startPoints.length >> 1) - 1);
        assertNumberBetween(toIndex, 0, (this.#startPoints.length >> 1) - 1);
        assertNumberBetween(fromIndex, 0, (this.#endPoints.length >> 1) - 1);
        assertNumberBetween(toIndex, 0, (this.#endPoints.length >> 1) - 1);

        const move = <T>(array: T[], fromIndex: number, toIndex: number) => {
            const point = array[fromIndex];
            array.splice(fromIndex, 1);
            array.splice(toIndex, 0, point);
            return array;
        };

        const startCornerRadii = this._getCornerRadiusPerPoint(PolygonMask.START_POINTS);
        const endCornerRadii = this._getCornerRadiusPerPoint(PolygonMask.END_POINTS);
        this.startPoints = toFlatArray(move(toPointArray(this.#startPoints), fromIndex, toIndex));
        this.endPoints = toFlatArray(move(toPointArray(this.#endPoints), fromIndex, toIndex));
        this.startCornerRadii = move(startCornerRadii, fromIndex, toIndex);
        this.endCornerRadii = move(endCornerRadii, fromIndex, toIndex);
    }
}

function assertCornerRadii(radii: readonly number[]) {
    assertArray(radii);
    radii.forEach((radius) => {
        assertNumber(radius);
        assertMinimum(radius, 0);
    });
}

/**
 * Given a flat array, return a point array:
 * [1, 2, 3, 4] => [{ x: 1, y: 2 }, { x: 3, y: 4 }].
 *
 * @param array list of numbers
 */
function toPointArray(array: readonly number[]) {
    const result = [];

    for (let i = 0; i < array.length; i += 2) {
        result.push({ x: array[i], y: array[i + 1] });
    }

    return result;
}

/**
 * Given a point array, return a flat array:
 * [{ x: 1, y: 2 }, { x: 3, y: 4 }] => [1, 2, 3, 4].
 *
 * @param array array of points
 */
function toFlatArray(array: Point[]) {
    return array.reduce<number[]>((acc, point) => {
        acc.push(point.x, point.y);
        return acc;
    }, []);
}
